import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserCircle2, ArrowRight, X } from "lucide-react";

/**
 * Friendly reminder shown on the student dashboard until the student finishes
 * their profile (required for payment / receipt submission).
 *
 * The state is loaded fresh from `/student/profile/completion-status` on mount.
 * Once `is_complete` is true, the banner renders nothing.
 */
export default function ProfileIncompleteBanner({ next = "/student" }) {
  const [completion, setCompletion] = useState(null);
  const [accessState, setAccessState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/student/profile/completion-status").then((r) => r.data).catch(() => null),
      api.get("/student/access-status").then((r) => r.data).catch(() => null),
    ]).then(([c, a]) => {
      setCompletion(c);
      setAccessState(a);
    });
  }, []);

  if (!completion || completion.is_complete || dismissed) return null;
  // Per product spec: profile-completion reminder only makes sense once the
  // student has actually enrolled in a course (because the gate fires *before
  // payment*, and there's no payment without an enrolled course). Brand-new
  // students with no course see the <NoEnrolledCourseBanner /> instead.
  if (!accessState || !accessState.has_enrolled_course) return null;
  // Once a student has paid (full_access), profile completion is no longer a
  // payment blocker — silently hide the reminder there too.
  if (accessState.is_full) return null;

  return (
    <Card
      className="p-4 sm:p-5 border-2 border-amber-300 dark:border-amber-700 bg-amber-50/80 dark:bg-amber-900/30"
      data-testid="profile-incomplete-banner"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0 bg-amber-100 dark:bg-amber-800/50 text-amber-700 dark:text-amber-300">
          <UserCircle2 className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-amber-900 dark:text-amber-100">
            Complete your profile to proceed with course payment and enrollment verification.
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
            We just need a few more details to confirm your enrollment.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="profile-banner-complete-btn">
              <Link to={`/student/profile/complete?next=${encodeURIComponent(next)}`}>
                Complete Profile <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDismissed(true)} data-testid="profile-banner-dismiss" className="text-amber-800 hover:text-amber-900 dark:text-amber-200">
              <X className="w-4 h-4 mr-1" /> Dismiss for now
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
